import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import AdminLogin, { checkAdminAuth, clearAdminAuth } from './components/AdminLogin';
import AdminNoticeTab from './components/AdminNoticeTab';
import AdminGalleryTab from './components/AdminGalleryTab';

interface Consultation {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  course: string;
  message: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

const STATUS_OPTIONS = ['대기중', '상담완료', '등록완료', '취소'];
const STATUS_COLORS: Record<string, string> = {
  '대기중': 'bg-amber-100 text-amber-700',
  '상담완료': 'bg-[#1B3A6B]/10 text-[#1B3A6B]',
  '등록완료': 'bg-green-100 text-green-700',
  '취소': 'bg-gray-100 text-gray-500',
};

type AdminTab = 'consultation' | 'notice' | 'gallery';

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState<boolean>(checkAdminAuth());
  const [activeTab, setActiveTab] = useState<AdminTab>('consultation');
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('전체');
  const [filterCourse, setFilterCourse] = useState<string>('전체');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchConsultations = async () => {
    setLoading(true);
    const { data } = await supabase.from('consultations').select('*').order('created_at', { ascending: false });
    setConsultations(data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthed) { fetchConsultations(); }
  }, [isAuthed]);

  if (!isAuthed) {
    return <AdminLogin onSuccess={() => setIsAuthed(true)} />;
  }

  const handleStatusChange = async (id: number, newStatus: string) => {
    setUpdatingId(id);
    await supabase.from('consultations').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', id);
    setConsultations((prev) => prev.map((c) => c.id === id ? { ...c, status: newStatus } : c));
    setUpdatingId(null);
  };

  const courses = ['전체', ...Array.from(new Set(consultations.map((c) => c.course)))];
  const statuses = ['전체', ...STATUS_OPTIONS];

  const filtered = consultations.filter((c) => {
    const matchStatus = filterStatus === '전체' || c.status === filterStatus;
    const matchCourse = filterCourse === '전체' || c.course === filterCourse;
    const matchSearch = !searchQuery || c.name.includes(searchQuery) || c.phone.includes(searchQuery) || (c.email || '').includes(searchQuery);
    return matchStatus && matchCourse && matchSearch;
  });

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = consultations.filter((c) => c.status === s).length;
    return acc;
  }, {} as Record<string, number>);

  const tabs: { key: AdminTab; label: string; icon: string }[] = [
    { key: 'consultation', label: '상담 신청 내역', icon: 'ri-customer-service-line' },
    { key: 'notice', label: '공지사항 관리', icon: 'ri-notification-3-line' },
    { key: 'gallery', label: '갤러리 관리', icon: 'ri-image-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* 헤더 */}
        <section className="bg-[#1B3A6B] py-10">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => navigate('/')} className="text-white/50 text-xs hover:text-white transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-home-line" /> 홈
                </button>
                <i className="ri-arrow-right-s-line text-white/30 text-xs" />
                <span className="text-[#C9A84C] text-xs font-semibold">관리자</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-white">관리자 대시보드</h1>
              <p className="text-white/60 text-sm mt-1">대한문신사중앙회 중구지부</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchConsultations}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white text-sm font-semibold cursor-pointer hover:bg-white/20 transition-colors"
              >
                <i className="ri-refresh-line" /> 새로고침
              </button>
              <button
                onClick={() => { clearAdminAuth(); setIsAuthed(false); }}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-md bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-semibold cursor-pointer hover:bg-red-500/30 transition-colors"
              >
                <i className="ri-logout-box-line" /> 로그아웃
              </button>
            </div>
          </div>
        </section>

        {/* 탭 네비게이션 */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-0">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
                    activeTab === tab.key
                      ? 'border-[#1B3A6B] text-[#1B3A6B]'
                      : 'border-transparent text-gray-500 hover:text-[#1B3A6B] hover:border-gray-200'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                  {tab.key === 'consultation' && (
                    <span className="ml-1 bg-[#1B3A6B]/10 text-[#1B3A6B] text-xs font-bold px-2 py-0.5 rounded-full">
                      {consultations.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        {activeTab === 'consultation' && (
          <>
            {/* 통계 카드 */}
            <section className="bg-[#F4F6FA] border-b border-gray-200 py-5">
              <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {STATUS_OPTIONS.map((s) => (
                    <div
                      key={s}
                      onClick={() => setFilterStatus(filterStatus === s ? '전체' : s)}
                      className={`bg-white rounded-xl px-5 py-4 border cursor-pointer transition-all ${filterStatus === s ? 'border-[#1B3A6B] ring-1 ring-[#1B3A6B]/20' : 'border-gray-100 hover:border-[#1B3A6B]/30'}`}
                    >
                      <p className="text-gray-500 text-xs mb-1">{s}</p>
                      <p className="text-2xl font-black text-[#1B3A6B]">{counts[s] || 0}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[s]}`}>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 필터 & 검색 */}
            <section className="py-6 bg-white border-b border-gray-100">
              <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative flex-1 max-w-xs">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text" placeholder="이름, 연락처, 이메일 검색" value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-[#1B3A6B] bg-white"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-sm border border-gray-200 rounded-md px-3 py-2.5 bg-white cursor-pointer focus:outline-none focus:border-[#1B3A6B]"
                  >
                    {statuses.map((s) => <option key={s} value={s}>{s === '전체' ? '전체 상태' : s}</option>)}
                  </select>
                  <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}
                    className="text-sm border border-gray-200 rounded-md px-3 py-2.5 bg-white cursor-pointer focus:outline-none focus:border-[#1B3A6B]"
                  >
                    {courses.map((c) => <option key={c} value={c}>{c === '전체' ? '전체 과정' : c}</option>)}
                  </select>
                  {(filterStatus !== '전체' || filterCourse !== '전체' || searchQuery) && (
                    <button
                      onClick={() => { setFilterStatus('전체'); setFilterCourse('전체'); setSearchQuery(''); }}
                      className="whitespace-nowrap text-sm text-gray-500 border border-gray-200 rounded-md px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-1"
                    >
                      <i className="ri-close-line" /> 초기화
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 ml-auto">{filtered.length}건 표시</p>
              </div>
            </section>

            {/* 목록 */}
            <section className="py-8 bg-white">
              <div className="max-w-7xl mx-auto px-6">
                {loading ? (
                  <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#1B3A6B] border-t-transparent rounded-full animate-spin" /></div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <i className="ri-inbox-line text-4xl mb-3 block" />
                    <p className="text-sm">신청 내역이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filtered.map((c) => (
                      <div key={c.id} className="border border-gray-100 rounded-xl overflow-hidden">
                        <div
                          className="flex flex-col md:flex-row md:items-center gap-3 p-5 cursor-pointer hover:bg-[#F4F6FA] transition-colors"
                          onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                        >
                          <span className="text-xs text-gray-400 font-mono w-8 flex-shrink-0">#{c.id}</span>
                          <div className="flex-shrink-0 min-w-[120px]">
                            <p className="text-sm font-bold text-gray-800">{c.name}</p>
                            <p className="text-xs text-gray-500">{c.phone}</p>
                          </div>
                          <div className="flex-1">
                            <span className="inline-block text-xs bg-[#1B3A6B]/10 text-[#1B3A6B] font-semibold px-2.5 py-1 rounded-sm">{c.course}</span>
                          </div>
                          <p className="text-xs text-gray-400 flex-shrink-0 hidden md:block">{c.email || '-'}</p>
                          <p className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                            {new Date(c.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                          </p>
                          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={c.status}
                              onChange={(e) => handleStatusChange(c.id, e.target.value)}
                              disabled={updatingId === c.id}
                              className={`text-xs font-semibold px-3 py-1.5 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-500'}`}
                            >
                              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                          {selectedId === c.id
                            ? <i className="ri-arrow-up-s-line text-gray-300 flex-shrink-0 hidden md:block" />
                            : <i className="ri-arrow-down-s-line text-gray-300 flex-shrink-0 hidden md:block" />
                          }
                        </div>
                        {selectedId === c.id && (
                          <div className="px-6 pb-6 pt-2 bg-[#F4F6FA] border-t border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-bold text-gray-500 mb-1">신청자 정보</p>
                                <div className="bg-white rounded-lg p-4 border border-gray-100 space-y-2">
                                  {[
                                    { label: '이름', value: c.name },
                                    { label: '연락처', value: c.phone, href: `tel:${c.phone}` },
                                    { label: '이메일', value: c.email || '-', href: c.email ? `mailto:${c.email}` : undefined },
                                    { label: '희망 과정', value: c.course },
                                    { label: '신청일', value: new Date(c.created_at).toLocaleString('ko-KR') },
                                  ].map((row) => (
                                    <div key={row.label} className="flex gap-3">
                                      <span className="text-xs text-gray-400 w-16 flex-shrink-0">{row.label}</span>
                                      {row.href
                                        ? <a href={row.href} className="text-sm font-semibold text-[#1B3A6B] cursor-pointer hover:underline">{row.value}</a>
                                        : <span className="text-sm font-semibold text-gray-800">{row.value}</span>
                                      }
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-500 mb-1">문의 내용</p>
                                <div className="bg-white rounded-lg p-4 border border-gray-100 min-h-[120px]">
                                  {c.message
                                    ? <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                                    : <p className="text-sm text-gray-400 italic">문의 내용 없음</p>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === 'notice' && (
          <section className="py-8 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <AdminNoticeTab />
            </div>
          </section>
        )}

        {activeTab === 'gallery' && (
          <section className="py-8 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <AdminGalleryTab />
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
